import type Phaser from "phaser";
import { Capacitor } from "@capacitor/core";
import type { ScrollableList } from "@yjsgames/phaser-ui";
import type { CrazyGamesBannerSurface } from "../ads/CrazyGamesBannerSlot.js";
import {
  clearCrazyGamesBannerSlot,
  setCrazyGamesBannerSlotFromPhaser,
} from "../ads/CrazyGamesBannerSlot.js";
import {
  shouldShowCrazyGamesBannerPlaceholder,
  shouldReserveCrazyGamesBannerSlot,
  subscribeCrazyGamesBannerReadinessChange,
} from "../ads/CrazyGamesBannerReadiness.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { FONT_FAMILY, TYPO } from "./uiTokens.js";

export interface InlineBannerSlotLocal {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface InlineBannerPlaceholder {
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly label: Phaser.GameObjects.Text;
}

export type DevicePreviewMode = "mobile" | "web";

export interface InlineBannerPolicyInput {
  readonly isNativePlatform: boolean;
  readonly devicePreviewMode?: DevicePreviewMode;
}

export function shouldMountCrazyGamesInlineBanner(input: InlineBannerPolicyInput): boolean {
  return !input.isNativePlatform && input.devicePreviewMode !== "mobile";
}

export function readDevicePreviewMode(): DevicePreviewMode | undefined {
  if (typeof document === "undefined") return undefined;
  const mode = document.documentElement.dataset["dvpMode"];
  return mode === "mobile" || mode === "web" ? mode : undefined;
}

export function shouldMountCrazyGamesInlineBannerAtRuntime(): boolean {
  return shouldMountCrazyGamesInlineBanner({
    isNativePlatform: Capacitor.isNativePlatform(),
    devicePreviewMode: readDevicePreviewMode(),
  });
}

export const createInlineBannerPlaceholder = (
  scene: Phaser.Scene,
  slot: Pick<InlineBannerSlotLocal, "width" | "height">,
): InlineBannerPlaceholder => {
  const placeholder = {
    frame: scene.add
    .rectangle(0, 0, slot.width, slot.height, PALETTE_NUM.overlayDim, 0.2)
    .setOrigin(0, 0)
    .setStrokeStyle(1, PALETTE_NUM.accentGold, 0.7)
    .setDepth(1),
  label: scene.add
    .text(0, 0, "AD", {
      fontFamily: FONT_FAMILY,
      fontSize: TYPO.micro,
      color: PALETTE.accentGold,
    })
    .setOrigin(0.5)
    .setDepth(2),
  };
  setInlineBannerPlaceholderVisible(placeholder, shouldShowCrazyGamesBannerPlaceholder());
  return placeholder;
};

export function setInlineBannerPlaceholderVisible(
  placeholder: InlineBannerPlaceholder | null,
  visible: boolean,
): void {
  placeholder?.frame.setVisible(visible);
  placeholder?.label.setVisible(visible);
}

export const addInlineBannerPlaceholder = (
  scrollList: ScrollableList,
  placeholder: InlineBannerPlaceholder,
  slot: InlineBannerSlotLocal,
): void => {
  scrollList.addContent(placeholder.frame, slot.x, slot.y);
  scrollList.addContent(placeholder.label, slot.x + slot.width / 2, slot.y + slot.height / 2);
};

export const pushInlineBannerRelayout = (
  positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[],
  placeholder: InlineBannerPlaceholder,
  slot: InlineBannerSlotLocal,
): void => {
  positions.push(
    { obj: placeholder.frame, lx: slot.x, ly: slot.y },
    { obj: placeholder.label, lx: slot.x + slot.width / 2, ly: slot.y + slot.height / 2 },
  );
};

export const syncScrollableInlineBannerSlot = (
  surface: CrazyGamesBannerSurface,
  scene: Phaser.Scene,
  scrollList: ScrollableList,
  slot: InlineBannerSlotLocal,
  visible: boolean,
): void => {
  if (!visible) {
    clearCrazyGamesBannerSlot(surface);
    return;
  }
  const slotY = scrollList.y + slot.y - scrollList.scrollY;
  const inView = slotY + slot.height > scrollList.y && slotY < scrollList.y + scrollList.viewHeight;
  if (!inView) {
    clearCrazyGamesBannerSlot(surface);
    return;
  }
  setCrazyGamesBannerSlotFromPhaser(surface, scene, {
    x: scrollList.x + slot.x,
    y: slotY,
    width: slot.width,
    height: slot.height,
  });
};

export class ScrollInlineBannerSlot {
  private readonly placeholder: InlineBannerPlaceholder | null;
  private slot: InlineBannerSlotLocal;
  private readonly enabled: boolean;

  constructor(
    private readonly surface: CrazyGamesBannerSurface,
    private readonly scene: Phaser.Scene,
    private readonly scrollList: ScrollableList,
    slot: InlineBannerSlotLocal,
    enabled = shouldMountCrazyGamesInlineBannerAtRuntime(),
  ) {
    this.slot = slot;
    this.enabled = enabled;
    this.placeholder = enabled ? createInlineBannerPlaceholder(scene, slot) : null;
    if (this.placeholder) addInlineBannerPlaceholder(scrollList, this.placeholder, slot);
  }

  pushRelayout(
    positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[],
    slot: InlineBannerSlotLocal,
  ): void {
    this.slot = slot;
    if (this.placeholder) pushInlineBannerRelayout(positions, this.placeholder, slot);
  }

  sync(visible: boolean): void {
    setInlineBannerPlaceholderVisible(this.placeholder, shouldShowCrazyGamesBannerPlaceholder());
    if (!this.enabled || !shouldReserveCrazyGamesBannerSlot()) {
      clearCrazyGamesBannerSlot(this.surface);
      return;
    }
    syncScrollableInlineBannerSlot(this.surface, this.scene, this.scrollList, this.slot, visible);
  }

  setPlaceholderVisible(visible: boolean): void {
    setInlineBannerPlaceholderVisible(this.placeholder, visible);
  }
}

export const mountFixedInlineBannerSlot = (
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  surface: CrazyGamesBannerSurface,
  slot: InlineBannerSlotLocal,
  enabled = shouldMountCrazyGamesInlineBannerAtRuntime(),
): void => {
  if (!enabled || !shouldReserveCrazyGamesBannerSlot()) {
    clearCrazyGamesBannerSlot(surface);
    return;
  }
  const placeholder = createInlineBannerPlaceholder(scene, slot);
  placeholder.frame.setPosition(slot.x, slot.y);
  placeholder.label.setPosition(slot.x + slot.width / 2, slot.y + slot.height / 2);
  container.add(placeholder.frame);
  container.add(placeholder.label);
  const sync = (): void => {
    setInlineBannerPlaceholderVisible(placeholder, shouldShowCrazyGamesBannerPlaceholder());
    if (!shouldReserveCrazyGamesBannerSlot()) {
      clearCrazyGamesBannerSlot(surface);
      return;
    }
    setCrazyGamesBannerSlotFromPhaser(surface, scene, slot);
  };
  const unsubscribe = subscribeCrazyGamesBannerReadinessChange(sync);
  scene.events.once("shutdown", unsubscribe);
  container.once("destroy", unsubscribe);
  sync();
};
